import normalizeWheel from 'normalize-wheel'
import { TweenLite } from 'gsap';

import TimeMixin from '@/mixins/Time.js'
import MouseMixin from '@/mixins/Mouse.js'
import SizesMixin from '@/mixins/Sizes.js'
import EventHubMixin from '@/mixins/EventHub.js'
import APIMixin from '@/mixins/API.js'

import AppPicture from '@/components/app-picture'
import AppIconPlay from '@/components/app-icon-play'

export default
{
    name: 'app-grid',

    components:
    {
        AppPicture,
        AppIconPlay
    },

    mixins:
    [
        TimeMixin,
        MouseMixin,
        SizesMixin,
        EventHubMixin,
        APIMixin
    ],

    data()
    {
        return {
            highlightVisible: false,
            itemSize: 0,
            activeIndex: - 1,
            scale: 1,
            gridData: [],
            overlayName: null,
            grabbing: false,
            focused: false,
            debugActive: false
        }
    },

    computed:
    {
        active()
        {
            return ['stories'].indexOf(this.$route.meta.name) !== - 1
        },

        loadPictures()
        {
            return ['stories', 'menu', 'home', 'chapters', 'chapter', '404'].indexOf(this.$route.meta.name) !== - 1
        }
    },

    watch:
    {
        $route()
        {
            const shouldShowItems = this.$route.meta.name !== 'home'

            if(shouldShowItems !== this.shouldShowItems)
            {
                for(const _item of this.items)
                {
                    window.setTimeout(() =>
                    {
                        _item.$element.classList.add('visible')
                    }, 500 + Math.random() * 2000)
                }
            }

            this.shouldShowItems = shouldShowItems
            this.unfocus()

            // Overlays
            this.updateOverlay()

            // Visible
            const visible = ['stories', 'menu', 'home', 'chapters', 'chapter', '404'].indexOf(this.$route.meta.name) !== - 1

            if(visible)
            {
                this.visible = true
            }
            else
            {
                window.setTimeout(() =>
                {
                    this.visible = ['stories', 'menu', 'home', 'chapters', 'chapter', '404'].indexOf(this.$route.meta.name) !== - 1
                }, 1000)
            }
        }
    },

    created()
    {
        this.visible = ['stories', 'menu', 'home', 'chapters', 'chapter', '404'].indexOf(this.$route.meta.name) !== - 1

        this.loaded = false
        this.mounted = false
        this.started = false
        this.content = null
        this.sizesWidth = 0
        this.sizesHeight = 0
        this.shouldShowItems = this.$route.meta.name !== 'home'

        this.fetch('pages', { slug: 'stories' }, (data, error) =>
        {
            // Error
            if(error === true || data.length < 1)
            {
                this.$router.push({ name: '404' })
                return
            }

            this.loaded = true
            this.content = data[0]

            if(this.mounted)
            {
                this.start()
            }
        })

        this.$eventHub.$on('grid.setUnfocus', this.unfocus)
    },

    mounted()
    {
        this.mounted = true

        if(this.loaded)
        {
            this.start()
        }

        this.updateOverlay()
    },

    methods:
    {
        updateOverlay()
        {
            switch(this.$route.meta.name)
            {
                case 'about':
                case 'chapter':
                case 'story':
                    this.overlayName = 'general-grain'
                    break

                case 'chapters':
                    this.overlayName = this.sizes.width < 1024 ? 'bottom-fade' : 'left-fade'
                    break

                case 'home':
                    this.overlayName = 'general-grain'
                    break

                case 'menu':
                    this.overlayName = this.sizes.width < 1024 ? 'top-fade' : 'left-fade'
                    break

                case 'stories':
                case 'privacy-policy':
                case 'legal-notice':
                case 'terms-of-use':
                    this.overlayName = 'bottom-top-fade'
                    break
            }
        },

        start()
        {
            const rows = Math.ceil(Math.sqrt(this.content.acf.stories_on_the_grid.length))
            const gridTotal = rows * rows

            // Define blocks order
            let data = []
            for(let i = 0; i < gridTotal; i++)
            {
                const x = i % rows
                const y = Math.floor(i / rows)
                const distance = Math.hypot(x - (rows - 1) / 2, y - (rows - 1) / 2)

                data.push({
                    x,
                    y,
                    index: i,
                    distance
                })
            }
            data = data.sort((a, b) =>
            {
                return a.distance - b.distance
            })

            for(let i = 0; i < gridTotal; i++)
            {
                const _data = data[i]
                const contentIndex = i % this.content.acf.stories_on_the_grid.length
                const _content = this.content.acf.stories_on_the_grid[contentIndex]
                _data.id = _content.ID
                _data.order = i
                _data.type = _content.type

                _data.sources = []

                const keys = Object.keys(_content.image.sizes)
                for(const sizeKey of keys)
                {
                    const size = _content.image.sizes[sizeKey]
                    const sizeKeySplit = sizeKey.split('x')

                    if(sizeKeySplit[1] !== '0')
                    {
                        _data.sources.push(`${this.$root.config.api.url}${size} ${sizeKeySplit[0]}x${sizeKeySplit[1]}`)
                    }
                }

                _data.sources = _data.sources.join(',')
            }

            data = data.sort((a, b) =>
            {
                return a.index - b.index
            })

            this.gridData = data

            this.$nextTick(() =>
            {
                this.started = true

                this.setFocus()
                this.setItems()
                this.setParallax()
                this.setGesture()

                this.$eventHub.$on('headerTrigger.click', (data) =>
                {
                    if(data.action === 'unfocusGrid')
                    {
                        this.unfocus()
                    }
                })

                this.onResize()
            })
        },

        setItems()
        {
            this.locked = false
            this.needsUpdate = true

            this.$highlightPlaceholder = this.$refs.highlightPlaceholder

            // Elements
            this.$back = this.$refs.back
            this.$zoomer = this.$refs.zoomer
            this.$mover = this.$refs.mover
            this.$maskOverlay = this.$refs.maskOverlay

            // Square grid
            this.rows = Math.ceil(Math.sqrt(this.gridData.length))
            this.itemSize = 100 // Will be update on resize
            this.gridSize = this.rows * this.itemSize // Will be update on resize

            // Coordinates at center
            this.x = 0
            this.y = 0
            this.target = {}
            this.target.x = 0
            this.target.y = 0
            this.rounded = {}
            this.rounded.x = 0
            this.rounded.y = 0

            this.items = []

            // Each item
            for(let i = 0; i < this.gridData.length; i++)
            {
                const _data = this.gridData[i]
                const item = {}

                // Set up
                item.id = _data.id
                item.needsUpdate = true
                item.inScreen = false

                item.position = {}
                item.position.x = i % this.rows
                item.position.y = Math.floor(i / this.rows)
                // item.opacity = {}
                // item.opacity.target = 1
                // item.opacity.value = 1
                // item.opacity.rounded = 1

                // Element
                item.$element = this.$refs.element[i]
                item.$element.style.width = `${this.itemSize}px`
                item.$element.style.height = `${this.itemSize}px`
                item.$element.classList.add(`delay-${item.position.x + item.position.y}`)

                // Image container
                item.$imageContainer = item.$element.querySelector('.image-container')

                // Picture
                item.picture = this.$refs.picture[i]

                item.loadCallback = () =>
                {
                    if(this.shouldShowItems)
                    {
                        window.setTimeout(() =>
                        {
                            item.$element.classList.add('visible')
                        }, Math.random() * 1000)
                    }

                    item.picture.$off('loading-end', item.loadCallback)
                }
                item.picture.$on('loading-end', item.loadCallback)

                // Save
                this.items.push(item)
            }
        },

        setFocus()
        {
            this.focus = {}
            this.focus.item = null
            this.focus.active = false
            this.focused = false
        },

        focusItem(item, _coordinates = 'highlight')
        {
            if(this.locked)
            {
                return
            }

            // Reset speed
            this.gesture.speed.x = 0
            this.gesture.speed.y = 0
            this.gesture.trackBack.x = 0
            this.gesture.trackBack.y = 0

            // Header trigger
            if(this.sizes.width < 1024)
            {
                this.$eventHub.$emit('headerTrigger.setState', { state: 'close', event: { action: 'unfocusGrid' } })
            }

            let coordinates = null

            // Coordinates
            // Default
            if(typeof _coordinates === 'undefined')
            {
                coordinates = {}
                coordinates.x = 30
                coordinates.y = 60
                coordinates.size = 230
            }
            else if(_coordinates === 'highlight')
            {
                const boundings = this.$highlightPlaceholder

                coordinates = {}
                coordinates.x = boundings.x
                coordinates.y = boundings.y
                coordinates.size = boundings.width
            }
            else if(_coordinates === 'fit')
            {
                coordinates = {}
                if(this.sizes.viewport.width < this.sizes.viewport.height)
                {
                    coordinates.x = 0
                    coordinates.y = (this.sizes.viewport.height - this.sizes.viewport.width) * 0.5
                    coordinates.size = this.sizes.viewport.width
                }
                else
                {
                    coordinates.x = (this.sizes.viewport.width - this.sizes.viewport.height) * 0.5
                    coordinates.y = 0
                    coordinates.size = this.sizes.viewport.height
                }
            }
            else if(_coordinates === 'fill')
            {
                coordinates = {}
                if(this.sizes.viewport.width < this.sizes.viewport.height)
                {
                    coordinates.x = - (this.sizes.viewport.height - this.sizes.viewport.width) * 0.5
                    coordinates.y = 0
                    coordinates.size = this.sizes.viewport.height
                }
                else
                {
                    coordinates.x = 0
                    coordinates.y = - (this.sizes.viewport.width - this.sizes.viewport.height) * 0.5
                    coordinates.size = this.sizes.viewport.width
                }
            }

            this.scale = coordinates.size / this.itemSize
            const translateX = - item.position.x * this.itemSize + coordinates.x / this.scale - this.x
            const translateY = - item.position.y * this.itemSize + coordinates.y / this.scale - this.y

            this.$zoomer.style.transform = `translateX(${translateX}px) translateY(${translateY}px) scaleX(${this.scale}) scaleY(${this.scale})`
            this.$zoomer.style.transformOrigin = `${- translateX}px ${- translateY}px`

            this.$maskOverlay.classList.add('active')
            item.$element.classList.add('focused')

            // Parallax strength
            TweenLite.to(this.parallax, 1, { strength: 15 })

            this.focus.item = item
            this.focus.active = true
            this.focused = true
            this.locked = true
            this.activeIndex = this.items.indexOf(this.focus.item)

            this.$eventHub.$emit('grid.focusItem', this.focus.item)
        },

        updateItemSize()
        {

        },

        unfocus()
        {
            // Not started or focus not active
            if(!this.started || !this.focus.active)
            {
                return
            }

            // Header trigger change state
            this.$eventHub.$emit('headerTrigger.setState', { state: 'list', to: { name: `${this.$route.meta.lang}_menu` } })

            const zoomerTransitionEnd = (_event) =>
            {
                // Prevent trigger if it's not the zoomer element
                if(_event.target !== this.$zoomer)
                {
                    return
                }

                // Remove class
                this.focus.item.$element.classList.remove('focused')

                // Save
                this.focus.item = null
                this.locked = false
                this.activeIndex = - 1

                // Remove listener
                this.$zoomer.removeEventListener('transitionend', zoomerTransitionEnd)
            }

            // Add transition end listener
            this.$zoomer.addEventListener('transitionend', zoomerTransitionEnd)

            // Reset zoomer
            window.setTimeout(() =>
            {
                this.$zoomer.style.transform = 'translateX(0px) translateY(0px) scaleX(1) scaleY(1)'

                // Remove class
                this.$maskOverlay.classList.remove('active')

                // Parallax strength
                TweenLite.to(this.parallax, 1, { strength: 40 })

                this.focus.active = false
                this.focused = false
            }, 400)

            this.$eventHub.$emit('grid.unFocus')
        },

        setParallax()
        {
            this.parallax = {}
            this.parallax.target = {}
            this.parallax.target.x = 0
            this.parallax.target.y = 0
            this.parallax.smoothed = {}
            this.parallax.smoothed.x = 0
            this.parallax.smoothed.y = 0
            this.parallax.strength = 40
            this.parallax.easing = 0.005

            if(this.$root.config.platform.touch)
            {
                this.parallax.deviceOrientationCallback = (event) =>
                {
                    let x = 0
                    let y = 0

                    switch(window.orientation)
                    {
                        case 90:
                            x = event.beta * 0.04
                            y = (- event.gamma * 0.02 - Math.PI * 0.4)
                            break

                        case -90:
                            x = - event.beta * 0.04
                            y = (event.gamma * 0.02 - Math.PI * 0.4)
                            break

                        default:
                        case 0:
                            x = event.gamma * 0.03
                            y = (event.beta * 0.02 - Math.PI * 0.4)
                            break
                    }

                    // Limit X
                    if(x > 0.5)
                    {
                        x = 0.5
                    }
                    else if(x < - 0.5)
                    {
                        x = - 0.5
                    }

                    // Limit Y
                    if(y > 0.5)
                    {
                        y = 0.5
                    }
                    else if(y < - 0.5)
                    {
                        y = - 0.5
                    }

                    x *= this.parallax.strength
                    y *= this.parallax.strength

                    this.parallax.target.x = x
                    this.parallax.target.y = y
                }
                window.addEventListener('deviceorientation', this.parallax.deviceOrientationCallback, false)
            }
            else
            {
                this.parallax.mouseMoveCallback = (_event) =>
                {
                    this.parallax.target.x = ((_event.clientX / this.sizes.width) - 0.5) * this.parallax.strength
                    this.parallax.target.y = ((_event.clientY / this.sizes.height) - 0.5) * this.parallax.strength
                }
                window.addEventListener('mousemove', this.parallax.mouseMoveCallback)
            }
        },

        setGesture()
        {
            this.gesture = {}

            this.gesture.x = 0
            this.gesture.y = 0

            this.gesture.previous = {}
            this.gesture.previous.x = 0
            this.gesture.previous.y = 0

            this.gesture.speed = {}
            this.gesture.speed.x = 0
            this.gesture.speed.y = 0

            this.gesture.elastic = {}
            this.gesture.elastic.strength = 5 // 4

            this.gesture.trackBack = {}
            this.gesture.trackBack.pow = 1
            this.gesture.trackBack.strength = 0.003 // 0.006
            this.gesture.trackBack.x = 0
            this.gesture.trackBack.y = 0

            /**
             * Touch
             */
            this.gesture.touchStart = (event) =>
            {
                // Inactive
                if(!this.active)
                {
                    return
                }

                // Locked
                if(this.locked)
                {
                    return
                }

                event.preventDefault()

                this.grabbing = true

                this.gesture.previous.x = event.touches[0].pageX
                this.gesture.previous.y = event.touches[0].pageY

                this.gesture.x = event.touches[0].pageX
                this.gesture.y = event.touches[0].pageY

                this.$el.addEventListener('touchmove', this.gesture.touchMove)
                window.addEventListener('touchend', this.gesture.touchEnd)
            }

            this.gesture.touchMove = (event) =>
            {
                // Inactive
                if(!this.active)
                {
                    return
                }

                event.preventDefault()

                this.gesture.x = event.touches[0].pageX
                this.gesture.y = event.touches[0].pageY
            }

            this.gesture.touchEnd = () =>
            {
                this.grabbing = false

                this.$el.removeEventListener('touchmove', this.gesture.touchMove)
                window.removeEventListener('touchend', this.gesture.touchEnd)
            }

            this.$el.addEventListener('touchstart', this.gesture.touchStart)

            /**
             * Mouse
             */
            this.gesture.mouseDown = (event) =>
            {
                // Inactive
                if(!this.active)
                {
                    return
                }

                event.preventDefault()

                // Locked
                if(this.locked)
                {
                    return
                }

                this.grabbing = true

                this.gesture.previous.x = event.clientX
                this.gesture.previous.y = event.clientY

                this.gesture.x = event.clientX
                this.gesture.y = event.clientY

                this.$el.addEventListener('mousemove', this.gesture.mouseMove)
                window.addEventListener('mouseup', this.gesture.mouseUp)
            }

            this.gesture.mouseMove = (event) =>
            {
                // Inactive
                if(!this.active)
                {
                    return
                }

                event.preventDefault()

                this.gesture.x = event.clientX
                this.gesture.y = event.clientY
            }

            this.gesture.mouseUp = () =>
            {
                this.grabbing = false

                this.$el.removeEventListener('mousemove', this.gesture.mouseMove)
                window.removeEventListener('mouseup', this.gesture.mouseUp)
            }

            this.$el.addEventListener('mousedown', this.gesture.mouseDown)

            /**
             * Wheel
             */
            const wheelCallback = (event) =>
            {
                // Inactive
                if(!this.active)
                {
                    return
                }

                // Locked
                if(this.locked)
                {
                    return
                }

                event.preventDefault()

                const normalized = normalizeWheel(event)

                // Calculate delta
                let gestureDeltaX = - normalized.pixelX
                let gestureDeltaY = - normalized.pixelY

                this.gesture.previous.x = this.gesture.x
                this.gesture.previous.y = this.gesture.y

                // Reduce gestureDelta if out of bound
                if(this.x > 0 && gestureDeltaX > 0)
                {
                    const elasticStrength = 1 - (this.x / this.sizes.width) * this.gesture.elastic.strength
                    gestureDeltaX *= elasticStrength
                }
                else if(this.x < - this.gridSize + this.sizes.viewport.width && gestureDeltaX < 0)
                {
                    const elasticStrength = 1 - Math.abs((this.x + this.gridSize - this.sizes.viewport.width) / this.sizes.width) * this.gesture.elastic.strength
                    gestureDeltaX *= elasticStrength
                }
                if(this.y > 0 && gestureDeltaY > 0)
                {
                    const elasticStrength = 1 - (this.y / this.sizes.height) * this.gesture.elastic.strength
                    gestureDeltaY *= elasticStrength
                }
                else if(this.y < - this.gridSize + this.sizes.viewport.height && gestureDeltaY < 0)
                {
                    const elasticStrength = 1 - Math.abs((this.y + this.gridSize - this.sizes.viewport.height) / this.sizes.height) * this.gesture.elastic.strength
                    gestureDeltaY *= elasticStrength
                }

                // Use drag value
                this.target.x = this.target.x + gestureDeltaX
                this.target.y = this.target.y + gestureDeltaY

                const x = this.x + (this.target.x - this.x) * 0.02 * this.time.delta
                const y = this.y + (this.target.y - this.y) * 0.02 * this.time.delta

                // Update speed
                this.gesture.speed.x = x - this.x
                this.gesture.speed.y = y - this.y
            }

            document.addEventListener('wheel', wheelCallback)
            document.addEventListener('mousewheel', wheelCallback)
            document.addEventListener('DOMMouseScroll', wheelCallback)

            /**
             * Click/Tap item
             */
            let startX = null
            let startY = null
            let distance = 0
            for(const item of this.items)
            {
                // Touch
                item.$element.addEventListener('touchstart', (event) =>
                {
                    event.preventDefault()

                    // Save touch position and reset distance
                    startX = event.touches[0].pageX
                    startY = event.touches[0].pageY
                    distance = 0

                    // Add event listeners
                    window.addEventListener('touchmove', touchMove)
                    window.addEventListener('touchend', touchEnd)
                })

                const touchMove = (event) =>
                {
                    // Update distance
                    distance = Math.sqrt(Math.pow(startX - event.touches[0].pageX, 2) + Math.pow(startY - event.touches[0].pageY, 2))
                }

                const touchEnd = () =>
                {
                    // Test if didn't move too much
                    if(distance < 12)
                    {
                        // Focus active
                        if(this.focus.active)
                        {
                            this.$eventHub.$emit('grid.chooseItem', this.focus.item)
                        }

                        // Focus not active
                        else
                        {
                            this.focusItem(item)
                        }
                    }

                    // Remove event listeners
                    window.removeEventListener('touchmove', touchMove)
                    window.removeEventListener('touchend', touchEnd)
                }

                // Mouse
                item.$element.addEventListener('mousedown', (event) =>
                {
                    // Save touch position and reset distance
                    startX = event.clientX
                    startY = event.clientY
                    distance = 0

                    // Add event listeners
                    window.addEventListener('mousemove', mouseMove)
                    window.addEventListener('mouseup', mouseUp)
                })

                const mouseMove = (event) =>
                {
                    // Update distance
                    distance = Math.sqrt(Math.pow(startX - event.clientX, 2) + Math.pow(startY - event.clientY, 2))
                }

                const mouseUp = () =>
                {
                    // Test if didn't move too much
                    if(distance < 12)
                    {
                        // Focus active
                        if(this.focus.active)
                        {
                            this.$eventHub.$emit('grid.chooseItem', this.focus.item)
                        }

                        // Focus not active
                        else
                        {
                            this.focusItem(item)
                        }
                    }

                    // Remove event listeners
                    window.removeEventListener('mousemove', mouseMove)
                    window.removeEventListener('mouseup', mouseUp)
                }
            }

            /**
             * Keyboard
             */
            this.gesture.keyDown = (event) =>
            {
                if(event.keyCode === 27 && this.focus.active)
                {
                    event.preventDefault()

                    this.unfocus()
                }

                if(event.altKey && event.keyCode === 68)
                {
                    this.debugActive = !this.debugActive
                }
            }
            window.addEventListener('keydown', this.gesture.keyDown)

            /**
             * Back
             */
            this.$back.addEventListener('click', (event) =>
            {
                event.preventDefault()

                if(this.focus.active)
                {
                    this.unfocus()
                }
            })

            /**
             * Overlay
             */
            this.$maskOverlay.addEventListener('click', (event) =>
            {
                event.preventDefault()

                if(this.focus.active)
                {
                    this.unfocus()
                }
            })
        },

        onResize()
        {
            // Not started yet
            if(!this.started)
            {
                return
            }

            // Same sizes
            if(this.sizes.viewport.width === this.sizesWidth && this.sizes.viewport.height === this.sizesHeight)
            {
                return
            }

            this.sizesWidth = this.sizes.viewport.width
            this.sizesHeight = this.sizes.viewport.height

            const minScreenSize = 320
            const maxScreenSize = 2600
            const screenSizeRatio = Math.min(Math.max((this.sizes.viewport.width - minScreenSize) / maxScreenSize, 0), 1)

            const maxItemSize = 360
            const minItemSize = 150

            let itemSize = minItemSize + ((screenSizeRatio) * (maxItemSize - minItemSize))

            itemSize = Math.round(itemSize)

            this.itemSize = itemSize
            this.gridSize = this.rows * this.itemSize

            this.$mover.style.width = `${this.gridSize}px`
            this.$mover.style.height = `${this.gridSize}px`

            this.x = - this.gridSize * 0.5 + this.sizes.viewport.width * 0.5
            this.y = - this.gridSize * 0.5 + this.sizes.viewport.height * 0.5

            // Update each item
            for(const item of this.items)
            {
                item.$element.style.width = `${this.itemSize}px`
                item.$element.style.height = `${this.itemSize}px`

                item.needsUpdate = true // Force item update in next tick
            }

            this.needsUpdate = true // Force grid update in next tick
        },

        onTick()
        {
            // Not started yet
            if(!this.started || !this.visible)
            {
                return
            }

            /**
             * Parallax
             */
            this.parallax.smoothed.x += (this.parallax.target.x - this.parallax.smoothed.x) * this.time.delta * this.parallax.easing
            this.parallax.smoothed.y += (this.parallax.target.y - this.parallax.smoothed.y) * this.time.delta * this.parallax.easing

            /**
             * Gesture
             */
            if(!this.focus.active)
            {
                // Is dragging
                if(this.grabbing)
                {
                    // Calculate delta
                    let gestureDeltaX = this.gesture.x - this.gesture.previous.x
                    let gestureDeltaY = this.gesture.y - this.gesture.previous.y

                    this.gesture.previous.x = this.gesture.x
                    this.gesture.previous.y = this.gesture.y

                    // Reduce gestureDelta if out of bound
                    if(this.x > 0 && gestureDeltaX > 0)
                    {
                        const elasticStrength = 1 - (this.x / this.sizes.width) * this.gesture.elastic.strength
                        gestureDeltaX *= elasticStrength
                    }
                    else if(this.x < - this.gridSize + this.sizes.viewport.width && gestureDeltaX < 0)
                    {
                        const elasticStrength = 1 - Math.abs((this.x + this.gridSize - this.sizes.viewport.width) / this.sizes.width) * this.gesture.elastic.strength
                        gestureDeltaX *= elasticStrength
                    }
                    if(this.y > 0 && gestureDeltaY > 0)
                    {
                        const elasticStrength = 1 - (this.y / this.sizes.height) * this.gesture.elastic.strength
                        gestureDeltaY *= elasticStrength
                    }
                    else if(this.y < - this.gridSize + this.sizes.viewport.height && gestureDeltaY < 0)
                    {
                        const elasticStrength = 1 - Math.abs((this.y + this.gridSize - this.sizes.viewport.height) / this.sizes.height) * this.gesture.elastic.strength
                        gestureDeltaY *= elasticStrength
                    }

                    // Use drag value
                    this.target.x = this.target.x + gestureDeltaX
                    this.target.y = this.target.y + gestureDeltaY

                    const x = this.x + (this.target.x - this.x) * 0.02 * this.time.delta
                    const y = this.y + (this.target.y - this.y) * 0.02 * this.time.delta

                    // Update speed
                    this.gesture.speed.x = x - this.x
                    this.gesture.speed.y = y - this.y

                    this.x = x
                    this.y = y
                }

                // Stopped dragging
                else
                {
                    if(this.x > 0)
                    {
                        this.gesture.trackBack.x = - Math.abs(Math.pow(this.x, this.gesture.trackBack.pow)) * this.gesture.trackBack.strength
                    }
                    else if(this.x < - this.gridSize + this.sizes.viewport.width)
                    {
                        this.gesture.trackBack.x = Math.abs(Math.pow(this.x + this.gridSize - this.sizes.viewport.width, this.gesture.trackBack.pow)) * this.gesture.trackBack.strength
                    }
                    else
                    {
                        this.gesture.trackBack.x = 0
                    }

                    if(this.y > 0)
                    {
                        this.gesture.trackBack.y = - Math.abs(Math.pow(this.y, this.gesture.trackBack.pow)) * this.gesture.trackBack.strength
                    }
                    else if(this.y < - this.gridSize + this.sizes.viewport.height)
                    {
                        this.gesture.trackBack.y = Math.abs(Math.pow(this.y + this.gridSize - this.sizes.viewport.height, this.gesture.trackBack.pow)) * this.gesture.trackBack.strength
                    }
                    else
                    {
                        this.gesture.trackBack.y = 0
                    }

                    // Use speed
                    this.x += this.gesture.speed.x
                    this.y += this.gesture.speed.y
                    this.target.x = this.x
                    this.target.y = this.y

                    this.gesture.speed.x += this.gesture.trackBack.x
                    this.gesture.speed.y += this.gesture.trackBack.y

                    // Dim speed
                    this.gesture.speed.x *= 0.92
                    this.gesture.speed.y *= 0.92

                    if(!this.active)
                    {
                        this.gesture.speed.x += Math.cos(this.time.elapsed * 0.0002) * 0.01
                        // this.gesture.speed.y += Math.sin(this.time.elapsed * 0.0002) * 0.01
                    }
                }
            }

            /**
             * Update container
             */
            const roundedX = Math.round((this.x - this.parallax.smoothed.x) * 10) / 10
            const roundedY = Math.round((this.y - this.parallax.smoothed.y) * 10) / 10

            const needsTranslateUpdate = roundedX !== this.rounded.x || roundedY !== this.rounded.y || this.needsUpdate

            this.rounded.x = roundedX
            this.rounded.y = roundedY

            if(needsTranslateUpdate)
            {
                this.$mover.style.transform = `translateX(${this.rounded.x}px) translateY(${this.rounded.y}px)`

                this.needsUpdate = false
            }

            /**
             * Update items
             */
            for(const item of this.items)
            {
                const translateX = Math.round(item.position.x * this.itemSize)
                const translateY = Math.round(item.position.y * this.itemSize)

                // Item in screen
                const left = translateX + this.x
                const right = left + this.itemSize
                const top = translateY + this.y
                const bottom = top + this.itemSize
                const inScreen = right >= 0 && left <= this.sizes.viewport.width && bottom >= 0 && top <= this.sizes.viewport.height
                let screenPositionRatioX = (left + this.itemSize) / (this.sizes.viewport.width + this.itemSize)
                let screenPositionRatioY = (top + this.itemSize) / (this.sizes.viewport.height + this.itemSize)

                screenPositionRatioX = Math.min(Math.max(screenPositionRatioX, 0), 1)
                screenPositionRatioY = Math.min(Math.max(screenPositionRatioY, 0), 1)

                const screenPositionRatioCenterX = screenPositionRatioX - 0.5
                const screenPositionRatioCenterY = screenPositionRatioY - 0.5

                // In screen
                if(inScreen !== item.inScreen)
                {
                    item.inScreen = inScreen

                    // if(item.inScreen)
                    // {
                    //     item.$element.classList.add('in-screen')
                    // }
                    // else
                    // {
                    //     item.$element.classList.remove('in-screen')
                    // }
                }

                // this.overlayName = 'bottom-top-fade'

                if(item.inScreen || item.needsUpdate)
                {
                    // // Opacity
                    // let opacity = null

                    // if(this.focus.active)
                    // {
                    //     opacity = this.focus.item === item ? 1 : 0.2
                    // }
                    // else
                    // {
                    //     switch(this.overlayName)
                    //     {
                    //         case 'left-fade':
                    //             opacity = Math.min(Math.max((screenPositionRatioX * 0.4) + 0, 0), 1)
                    //             break

                    //         case 'bottom-top-fade':
                    //             opacity = Math.min(Math.max((1 - screenPositionRatioY) * 2 + 0.2, 0), 1)
                    //             break
                    //     }
                    // }

                    // item.opacity.target = opacity
                    // item.opacity.value += (item.opacity.target - item.opacity.value) * 0.1
                    // const opacityRounded = Math.round(item.opacity.value * 1000) / 1000

                    // if(opacityRounded !== item.opacity.rounded)
                    // {
                    //     item.$element.style.opacity = Math.round(item.opacity.value * 1000) / 1000
                    //     item.opacity.rounded = opacityRounded
                    // }

                    // Translation
                    if(needsTranslateUpdate)
                    {
                        item.$element.style.transform = `translateX(${translateX}px) translateY(${translateY}px)`

                        const imageContainerTranslateX = Math.round(screenPositionRatioCenterX * (1 - 1 / 1.3) * 100 * 100) / 100
                        const imageContainerTranslateY = Math.round(screenPositionRatioCenterY * (1 - 1 / 1.3) * 100 * 100) / 100
                        item.$imageContainer.style.transform = `translateX(${imageContainerTranslateX}%) translateY(${imageContainerTranslateY}%)`

                        // Needs update
                        item.needsUpdate = false
                    }
                }
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/app-grid/script.js